import Slider from 'react-slick';
import { Typography, Stack, Skeleton } from '@mui/material';
import { renderIcon, trimDescription as trimText } from '../util';
import { removeHTMLTagsFromString } from '@/utils/removeHTMLTagsFromString';

const settings = {
  dots: true,
  infinite: false,
  arrows: false,
  slidesToShow: 3,
  slidesToScroll: 3,
  appendDots: (dots) => (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
      {dots.map((dot, index) => (
        <div
          key={index}
          style={{
            width:
              index === dots.findIndex((d) => d.props.className.includes('slick-active')) ? 16 : 10,
            height: 6,
            borderRadius: '26px',
            backgroundColor:
              index === dots.findIndex((d) => d.props.className.includes('slick-active'))
                ? '#3b82f6'
                : '#94a3b8',
            margin: '0 6px',
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </div>
  ),
};

type Props = {
  filteredItems?: any[];
  categories?: any[];
  itemStyle?: Record<string, any>;
  handleItemSelect: (item: any) => void;
  itemInnerStyle?: Record<string, any>;
  itemIconStyle?: Record<string, any>;
  rtl: boolean;
  navigationSettings?: any;
  itemChevronStyle?: Record<string, any>;
  showSkeleton: boolean;
  renderCategoryIcon?: (category: any) => JSX.Element;
};

export const NavItemsSlider = ({
  filteredItems,
  categories,
  itemStyle,
  handleItemSelect,
  itemInnerStyle,
  itemIconStyle,
  rtl,
  navigationSettings,
  itemChevronStyle,
  showSkeleton,
  renderCategoryIcon,
}: Props) => {
  return showSkeleton ? (
    <Skeleton variant="rounded" height={117} width="100%" />
  ) : (
    <div style={{ maxWidth: '100vw', width: '100%', zIndex: 10 }}>
      <Slider className="nav-slider" {...settings}>
        {filteredItems?.length > 0 &&
          filteredItems.map((item) => {
            return (
              <Stack
                id={item.id}
                key={item.id}
                sx={itemStyle}
                onClick={() => handleItemSelect(item)}
              >
                <Stack sx={itemInnerStyle}>
                  <Stack sx={itemIconStyle}>
                    {item?.tag?.webPathThumbnail && item?.tag?.navigationThumbnail ? (
                      <img
                        alt="Thumbnail"
                        src={`${process.env.NEXT_PUBLIC_CDN}/tours/${item.tourId}/tags/icons/${item.tag.navigationThumbnail}`}
                        style={{ width: '38px', height: '38px' }}
                      />
                    ) : (
                      renderIcon(item[item.type], 38, 38)
                    )}
                  </Stack>
                  <Stack
                    sx={{ textAlign: rtl ? 'right' : 'center', direction: rtl ? 'rtl' : 'ltr' }}
                  >
                    <Typography
                      fontSize={{ xs: '14px', sm: '16px' }}
                      fontWeight={{ xs: 400, sm: 500 }}
                      sx={{ color: '#101828' }}
                    >
                      {trimText(item[item.type]?.title || item?.title || item?.name, 15)}
                    </Typography>
                    <Typography
                      fontSize="14px"
                      fontWeight={400}
                      sx={{ display: { xs: 'none', sm: 'block' }, color: '#667085' }}
                    >
                      {navigationSettings?.descriptionEnabled &&
                        trimText(removeHTMLTagsFromString(item[item.type]?.description), 100)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  flex={1}
                  boxSizing="border-box"
                  sx={{
                    display: { xs: 'none', lg: 'flex' },
                    ':hover': {
                      background: 'rgba(32, 85, 255, 0.06)',
                    },
                  }}
                >
                  <Typography textAlign="start" fontSize="16px" sx={{ color: '#667085' }}>
                    {item?.category?.title}
                  </Typography>
                </Stack>

                <Stack sx={itemChevronStyle}>
                  <i className={`fa-solid fa-chevron-${rtl ? 'left' : 'right'}`} />
                </Stack>
              </Stack>
            );
          })}
        {categories?.length > 0 &&
          categories.map((category) => {
            return (
              <Stack
                key={category.id}
                width={{
                  xs: '100%', //  '114px',
                  lg: '154px',
                }}
                height={{ xs: '114px', lg: '94px' }}
                gap="6px"
                alignItems="center"
                justifyContent="center"
                p={{ xs: '10px', lg: '14px' }}
                overflow="hidden"
                sx={{
                  display: 'flex !important',
                  borderRadius: '8px',
                  border: '1px solid #EAECF0',
                  background: '#FFF',
                  cursor: 'pointer',
                }}
                onClick={() => handleItemSelect(category)}
              >
                <Stack
                  width="30px"
                  height="30px"
                  sx={{
                    background: category.backgroundColor || '#2055FF',
                    borderRadius: '8px',
                    p: '6px 5px',
                  }}
                >
                  {renderCategoryIcon(category)}
                </Stack>
                <Typography
                  fontSize="14px"
                  fontWeight={{ xs: 400, sm: 500 }}
                  textAlign="center"
                  whiteSpace="break-spaces"
                  width="inherit"
                  textOverflow="ellipsis"
                  p={{ xs: '0 10px', lg: '0 14px' }}
                  lineHeight={1}
                  sx={{ color: '#101828' }}
                >
                  {trimText(category.title, 15)}
                </Typography>
              </Stack>
            );
          })}
      </Slider>
    </div>
  );
};
